<template>
  <teleport to="body">
    <div
      v-if="modelValue"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Upload Permission (Guests)</h5>
          <button type="button" class="close" aria-label="Close" @click="close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <strong>Select Guests</strong>
            <span class="small text-muted" v-if="loading">Loading…</span>
            <span class="small" v-else>Selected: {{ finalSelectedCount }}</span>
          </div>

          <div v-if="!loading">
            <div v-if="guests.length === 0" class="text-center text-muted py-3">
              No guests found.
            </div>
            <ul v-else class="list-group">
              <li
                v-for="g in guests"
                :key="getUploadUserId(g)"
                class="list-group-item d-flex align-items-center justify-content-between"
              >
                <div class="d-flex align-items-center">
                  <img
                    :src="g.user_img || 'https://dummyimage.com/60x60/ddd/555.png&text=G'"
                    class="rounded mr-2"
                    style="width:36px;height:36px;object-fit:cover"
                    alt=""
                  />
                  <div>
                    <div class="font-weight-bold">{{ g.name || g.username || g.display_name || 'User' }}</div>
                    <div class="text-muted small">{{ g.username }}</div>
                  </div>
                </div>

                <button
                  class="btn btn-sm"
                  :class="isSelected(g) ? 'btn-danger' : 'btn-outline-primary'"
                  :disabled="saving || !getUploadUserId(g)"
                  @click="toggle(g)"
                  :title="!getUploadUserId(g) ? 'No user_id available for this guest' : ''"
                >
                  <i :class="isSelected(g) ? 'fas fa-minus mr-1' : 'fas fa-plus mr-1'"></i>
                  {{ isSelected(g) ? 'Remove' : 'Add' }}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="saving" @click="close">Cancel</button>
          <button class="btn btn-primary" :disabled="saving || finalSelectedCount===0" @click="save">
            <span v-if="!saving">Save</span>
            <span v-else><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</span>
          </button>
        </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, computed, watch } from 'vue'
import api from '@/services/api'

const props = defineProps({
  modelValue: { type: Boolean, default: false }, // v-model
  eventId: { type: [String, Number], required: true },
  sessionUserId: { type: [String, Number], required: true }
})
const emit = defineEmits(['update:modelValue', 'saved'])

const loading = ref(false)
const saving = ref(false)
const guests = ref([])

const existingIds = ref(new Set()) // upload_status = 1
const addIds = ref(new Set())
const removeIds = ref(new Set())

function close(){ emit('update:modelValue', false) }

function getUploadUserId(u){
  const v = u?.user_id ?? u?.userid ?? u?.userId
  return v != null && String(v).trim() !== '' ? String(v) : ''
}

function isSelected(g){
  const id = getUploadUserId(g)
  if (!id) return false
  const final = finalSelectedIds.value
  return final.has(id)
}

function toggle(g){
  const id = getUploadUserId(g)
  if (!id) return
  const originally = existingIds.value.has(id)
  const currently = finalSelectedIds.value.has(id)

  if (currently) {
    // turn OFF
    if (originally) removeIds.value = new Set([...removeIds.value, id])
    else {
      const next = new Set(addIds.value); next.delete(id); addIds.value = next
    }
  } else {
    // turn ON
    if (originally) {
      const next = new Set(removeIds.value); next.delete(id); removeIds.value = next
    } else addIds.value = new Set([...addIds.value, id])
  }
}

const finalSelectedIds = computed(()=>{
  const s = new Set(existingIds.value)
  for (const id of addIds.value) s.add(id)
  for (const id of removeIds.value) s.delete(id)
  return s
})
const finalSelectedCount = computed(()=> finalSelectedIds.value.size)

async function load(){
  try {
    loading.value = true
    const fd = new FormData()
    fd.append('userid', String(props.sessionUserId))
    fd.append('eventid', String(props.eventId))

    const res = await api.getInvitationDetail(fd)
    const payload = res?.data ?? res
    let list =
      Array.isArray(payload?.data) ? payload.data :
      Array.isArray(payload?.data?.data) ? payload.data.data :
      Array.isArray(payload?.list) ? payload.list : []

    list = Array.isArray(list)
      ? list.filter(g => String(g?.username || '').trim() !== '')
      : []

    guests.value = list

    const ex = new Set()
    for (const g of list) {
      const id = getUploadUserId(g)
      if (!id) continue
      if (Number(g?.upload_status) === 1) ex.add(id)
    }
    existingIds.value = ex
    addIds.value = new Set()
    removeIds.value = new Set()
  } catch (e) {
    console.error('UploadPermissions load error:', e)
    guests.value = []
    existingIds.value = new Set()
  } finally {
    loading.value = false
  }
}

async function save(){
  try {
    saving.value = true
    const ids = Array.from(finalSelectedIds.value)
    const fd = new FormData()
    fd.append('eventid', String(props.eventId))
    fd.append('user_ids', ids.join(','))

    let res
    if (typeof api.saveUploadPermissions === 'function') res = await api.saveUploadPermissions(fd)
    else if (typeof api.saveViewPermissions === 'function') res = await api.saveViewPermissions(fd)

    const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
    if (!ok) throw new Error('Failed to save upload permissions')

    emit('saved', ids)
    close()
  } catch (e) {
    console.error('saveUploadPermissions error:', e)
    alert('Could not save upload permissions.')
  } finally {
    saving.value = false
  }
}

watch(()=>props.modelValue,(open)=>{ if (open) load() })
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1050;
  overflow-y: auto;
}

.modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 760px;
}

.modal-content {
  border: none;
  border-radius: 18px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.25);
}

.modal-header,
.modal-footer {
  border-color: rgba(15, 23, 42, 0.08);
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 1rem;
  }
  .modal-dialog {
    max-width: 100%;
  }
}
</style>
